import { useEffect, useState } from "react"; /*{ useEffect, useState }*/
import "./App.css";
import AppNavBar from "./navigation/AppBar";
import "src/components/globalStyles/react-datepicker.css";
import "src/components/globalStyles/lightbox_style.css";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { enUS } from "@mui/x-date-pickers/locales";
import { Provider, useSelector } from "react-redux";
import { store } from "./redux/store/Store";
import { Grid } from "@mui/material";
import AppRoutes from "./navigation/AppRoutes";
import { ThemeProvider } from "@mui/material/styles";
import { StyledEngineProvider, Theme } from "@mui/material";
import darkTheme from "./themes/oc-dark-default";
import WebsocketClient from "./websockets/WebsocketClient";
import { AppStatusBar } from "./navigation/AppStatusBar/AppStatusBar";
import { Auth, API } from "aws-amplify";
import { openSnackbar, login, openDrawer, getClientSetting } from "./redux/actions/App.Actions";
import { setAircraftRowSettings } from "src/redux/actions/flights/FlightTimeline.Actions";
import { DialogModal } from "./components/DialogModal/DialogModal";
import { isDevEnv } from "./utils/index";
import CssBaseline from "@mui/material/CssBaseline";
import { setDefaultNode } from "./redux/actions/Mosaic.Actions";
import { setAircraftFilters, setAircraftSort } from "./redux/actions/flights/FlightTimeline.Actions";
import "src/components/globalStyles/react-datepicker.css";
import "src/components/globalStyles/lightbox_style.css";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider, useDragDropManager } from "react-dnd";
import { recordFreezeEvent, startFreezeMonitor } from "src/utils/freezeMonitor";
import { setClock } from "src/redux/actions/Heartbeat.Actions";
import OCLogin from "./pages/Login/OCLogin";
import { ClientSettingName, IBidSettingsValue, IClientSettings } from "@vmosolutions/oc-interfaces";
import { setBiddingSettings } from "./redux/actions/CrewSchedule/CrewSchedule.Actions";
import { getDocManagerSetting, getVacationBiddingSetting } from "./redux/reducers/permissionsReducer";
import { getUISettings } from "./redux/reducers/UISettingsReducer";
import { getCreditSettings } from "./redux/reducers/CreditSettingReducer";
import { fetchDispatchDesks } from "./redux/reducers/DispatchDeskReducer";
import { initDispatchDeskOpsLinkWatcher } from "./utils/dispatchDeskOpsLinkSync";
import { hasPermission, PolicyAction, PolicyResource } from "@vmosolutions/oc-interfaces";
import { retrieveFlightData } from "./redux/actions/flights/FlightData.Actions";
import("webdatarocks/theme/dark/webdatarocks.min.css");
const usLocale = enUS.components.MuiLocalizationProvider.defaultProps.localeText;
// Functional component that retrieves the actively selected theme (based on redux stored name) and wraps the application with it.
// Using mapstatetoprops doesn't work in this file as it's at the same level as the store provider so this alternative method is the solution.
const ThemeWrapper = (props: any): JSX.Element => {
  return (
    <StyledEngineProvider injectFirst>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        localeText={{
          ...usLocale,
          fieldMonthPlaceholder: (params) => (params.contentType === "letter" ? "MMM" : "MM"),
        }}
      >
        <ThemeProvider theme={darkTheme}>
          <Grid
            container
            direction={"column"}
            style={{
              backgroundColor: darkTheme.palette.background.default,
              flexGrow: 1,
              position: "relative",
            }}
          >
            {props.children}
          </Grid>
          <DialogModal fullscreen={true} />
        </ThemeProvider>
      </LocalizationProvider>
    </StyledEngineProvider>
  );
};
const retrieveRoles = async (usersub: string): Promise<any> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  try {
    const data: any = await API.get("skytrains", `/user?sub=${usersub}`, {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
    });
    store.dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "success",
        message: `Welcome to OC, ${data.firstName}. Your platform is now ready to use.`,
      })
    );
    return data;
  } catch (error) {
    return error;
  }
};

/** Retrieve and assign bidding settings if enabled for client */
const retrieveBiddingSettings = async (): Promise<IBidSettingsValue> => {
  const defaultBidSetting: IBidSettingsValue = {
    enabled: false,
    ranks: [{ prefRank: 0, label: "", color: "#000" }],
    displayPlannedCredit: false,
    displayAssignedNFATypes: [],
    displayBidPackageDaysBack: 0,
  };
  try {
    const biddingSettings: IClientSettings | null = await getClientSetting(ClientSettingName.CrewBidding);
    return biddingSettings !== null && biddingSettings?.setValue?.enabled
      ? (biddingSettings?.setValue as IBidSettingsValue)
      : defaultBidSetting;
  } catch (error) {
    return defaultBidSetting;
  }
};

/** Ends a react-dnd drag that is still active after the browser has finished it. */
const DragWatchdog = (): null => {
  const manager = useDragDropManager();
  useEffect(() => {
    // A native drag swallows clicks and key presses, so receiving one while react-dnd reports a drag means it never ended.
    const endStuckDrag = (event: Event): void => {
      if ((event instanceof KeyboardEvent && event.key !== "Escape") || !manager.getMonitor().isDragging()) {
        return;
      }
      recordFreezeEvent("ended a stuck drag", { itemType: String(manager.getMonitor().getItemType()) });
      manager.getActions().endDrag();
    };
    window.addEventListener("pointerdown", endStuckDrag, true);
    window.addEventListener("keydown", endStuckDrag, true);
    return () => {
      window.removeEventListener("pointerdown", endStuckDrag, true);
      window.removeEventListener("keydown", endStuckDrag, true);
    };
  }, [manager]);
  return null;
};

const AppComponent = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [appClock, setAppClock] = useState<NodeJS.Timeout | undefined>(undefined);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        const userAttributes = await retrieveRoles(currentUser.attributes.sub);
        const biddingSettings = await retrieveBiddingSettings();
        store.dispatch(setBiddingSettings(biddingSettings));
        store.dispatch(getDocManagerSetting());
        store.dispatch(getVacationBiddingSetting());
        store.dispatch(getUISettings());
        store.dispatch(getCreditSettings());
        currentUser.attributes = Object.assign(currentUser.attributes, userAttributes);
        setUser(currentUser);
        store.dispatch(login(currentUser));

        const WSClient: WebsocketClient = new WebsocketClient();
        WebsocketClient.init();

        // Start watching Redux for dispatch desk changes — any time desks
        // update (initial fetch, WS push, manual add/remove) the watcher
        // automatically re-syncs OpsLink subscriptions & events.
        initDispatchDeskOpsLinkWatcher();

        // Fetch dispatch desks on startup. The silent flag suppresses error
        // toasts for users without dispatch desk permissions.
        store.dispatch(fetchDispatchDesks({ silent: true }));

        // Pre-load ~48 hours of flight data on each side of now for users
        // with any DispatchDesk permission. This puts flights into Redux
        // early so dispatch-desk-related features have data available
        // without waiting for the user to open the Flight Timeline.
        if (hasPermission(currentUser.attributes.policies, PolicyResource.DispatchDesk, PolicyAction.Read)) {
          const now = new Date();
          const preloadStart = new Date(now.getTime() - 48 * 60 * 60 * 1000);
          const preloadEnd = new Date(now.getTime() + 48 * 60 * 60 * 1000);
          store.dispatch(retrieveFlightData(preloadStart, preloadEnd));
        }

        // Set all the filter defaults, etc.
        if (currentUser?.attributes?.appSettings?.flightTimelineRowHeightPercentage) {
          const percentage = currentUser?.attributes?.appSettings?.flightTimelineRowHeightPercentage;
          store.dispatch(setAircraftRowSettings(percentage));
        }
        if (currentUser?.attributes?.appSettings?.mosaicLayout) {
          store.dispatch(setDefaultNode(currentUser?.attributes?.appSettings?.mosaicLayout));
          store.dispatch(openDrawer());
        }
        if (currentUser?.attributes?.appSettings?.timelineAircraftFilters) {
          const savedFiltersObj = currentUser?.attributes?.appSettings?.timelineAircraftFilters;
          const savedFilters =
            savedFiltersObj?.aircraftFilter !== undefined || savedFiltersObj?.aircraftSort !== undefined
              ? savedFiltersObj
              : null;
          if (savedFilters) {
            // If saved filters are found in localstorage, apply them to redux store.
            store.dispatch(setAircraftFilters(savedFilters.aircraftFilter));
            store.dispatch(setAircraftSort(savedFilters.aircraftSort));
          } else throw new Error("Bad filter/sort saved setting.");
        }
      } catch (error) {
        setUser(null);
        store.dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: "Error logging in, please contact support for assistance.",
          })
        );
      }
      setIsLoading(false);
      return;
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const handleWheel = (event: WheelEvent): void => {};

    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, []);

  useEffect(() => {
    //On mount
    setAppClock(setInterval(() => store.dispatch(setClock(new Date())), 1000));
    const stopFreezeMonitor = startFreezeMonitor();

    //On unmount clear the interval
    return () => {
      WebsocketClient.close();
      clearInterval(appClock);
      stopFreezeMonitor();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DndProvider backend={HTML5Backend}>
      <DragWatchdog />
      <Provider store={store}>
        <ThemeWrapper>
          <CssBaseline />
          <AppNavBar />
          <AppRoutes />
          <AppStatusBar />
        </ThemeWrapper>
      </Provider>
    </DndProvider>
  );
};

const App = (): JSX.Element => {
  return isDevEnv() ? (
    <OCLogin>
      <AppComponent />
    </OCLogin>
  ) : (
    <AppComponent />
  );
};
export default App;
